import { useState } from "react"
import api from "../../utils/api"
import Icon from "../../components/Icon"
import ConfirmModal from "../../components/ConfirmModal"
import { formatDateShort, formatMoney } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import {
  buildAcceptConfirm,
  buildDeclineConfirm,
  describeAcceptResult,
  describeInvitationTitle,
  describeWalletState,
} from "./tenancyInvitationDisplay"

const MODULE_TOP_UP_PAYBILL = "4123905"

const WALLET_TONES = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  error: "border-red-200 bg-red-50 text-red-700",
}

function AmountRow({ label, value, strong = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className={`text-xs ${strong ? "font-bold text-slate-900" : "text-slate-600"}`}>{label}</span>
      <span className={`tabular-nums ${strong ? "text-sm font-black text-slate-900" : "text-xs font-semibold text-slate-800"}`}>
        {value}
      </span>
    </div>
  )
}

function PendingUnitCard({ invitation, accountReference, busy, onApprove, onDecline }) {
  const wallet = describeWalletState(invitation)
  return (
    <article className="rounded-2xl border border-amber-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
          <Icon name="hourglass-split" size="1.1rem" />
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-black leading-tight text-slate-900">{describeInvitationTitle(invitation)}</h4>
          <p className="mt-0.5 text-xs text-slate-500">
            From {invitation.managerName} · Move-in {formatDateShort(invitation.moveInDate)}
          </p>
        </div>
      </div>

      <div className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50 px-3">
        <AmountRow label="Deposit" value={formatMoney(invitation.deposit)} />
        <AmountRow label="First month's rent" value={formatMoney(invitation.firstMonthRent)} />
        <AmountRow label="You pay when you approve" value={formatMoney(invitation.totalDueNow)} strong />
        <AmountRow label="Then rent each month" value={formatMoney(invitation.monthlyRent)} />
      </div>

      <p className="mt-2 text-[11px] text-slate-500">
        Your Rentals wallet: <span className="font-bold text-slate-700">{formatMoney(invitation.walletBalance)}</span>
      </p>
      <p className={`mt-1.5 rounded-xl border px-3 py-2 text-xs font-medium ${WALLET_TONES[wallet.tone]}`}>
        {wallet.text}
        {wallet.tone === "warning" && (
          <span className="mt-0.5 block font-normal">
            M-PESA Paybill {MODULE_TOP_UP_PAYBILL} · Account {accountReference || "your ID number"}
          </span>
        )}
      </p>

      <p className="mt-2 text-[11px] text-slate-500">
        Only approve if you are really renting this unit. If you do not know this manager, decline.
      </p>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => onDecline(invitation)}
          disabled={busy}
          className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={() => onApprove(invitation)}
          disabled={busy || !invitation.canAccept}
          className="flex-[2] rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Approve &amp; Pay
        </button>
      </div>
    </article>
  )
}

// Units a manager assigned to the signed-in person, waiting for them. For each:
// what they will pay, whether their wallet is ready, and Approve & Pay or
// Decline. Approving moves them in and pays the deposit and first rent; after
// that, rent is automatic. Used in the tenant's Rentals view and in the
// dashboard bell's pop-up.
function PendingUnitApprovals({ invitations = [], accountReference = "", onChanged, onOpenRentals, emptyText = "" }) {
  const [confirm, setConfirm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)

  const answer = async () => {
    if (!confirm) return
    const { kind, invitation } = confirm
    setBusy(true)
    setNotice(null)
    try {
      if (kind === "approve") {
        const response = await api.post(
          `/frp/tenancy-invitations/${encodeURIComponent(invitation._id)}/accept`,
          { expectedAmount: invitation.totalDueNow },
        )
        // The new sign-in carries the tenant's Rentals access straight away.
        if (response?.data?.token) localStorage.setItem("token", response.data.token)
        setNotice({ tone: "success", text: describeAcceptResult(response?.data, invitation), approved: true })
      } else {
        await api.post(`/frp/tenancy-invitations/${encodeURIComponent(invitation._id)}/decline`)
        setNotice({ tone: "success", text: "Declined. Nothing was charged." })
      }
    } catch (err) {
      setNotice({ tone: "error", text: getApiErrorMessage(err, "Could not save your answer. Please try again.") })
    } finally {
      setConfirm(null)
      setBusy(false)
      // Amounts or the assignment itself may have changed; show the current state.
      onChanged?.()
    }
  }

  const confirmContent = confirm
    ? confirm.kind === "approve" ? buildAcceptConfirm(confirm.invitation) : buildDeclineConfirm(confirm.invitation)
    : null

  return (
    <>
      {notice && (
        <div
          role={notice.tone === "error" ? "alert" : "status"}
          className={`mb-3 rounded-xl border px-3 py-2 text-xs font-medium ${
            notice.tone === "error"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          {notice.text}
          {notice.approved && onOpenRentals && (
            <button
              type="button"
              onClick={onOpenRentals}
              className="mt-2 block rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"
            >
              Open Rentals
            </button>
          )}
        </div>
      )}

      {invitations.length === 0 ? (
        !notice && emptyText && <p className="py-6 text-center text-sm text-slate-500">{emptyText}</p>
      ) : (
        <div className="space-y-3">
          {invitations.map((invitation) => (
            <PendingUnitCard
              key={invitation._id}
              invitation={invitation}
              accountReference={accountReference}
              busy={busy}
              onApprove={(item) => setConfirm({ kind: "approve", invitation: item })}
              onDecline={(item) => setConfirm({ kind: "decline", invitation: item })}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirmContent?.title}
        message={confirmContent?.message}
        details={confirmContent?.details || []}
        icon={confirm?.kind === "approve" ? "wallet2" : "x-circle-fill"}
        tone={confirm?.kind === "approve" ? "teal" : "rose"}
        confirmLabel={confirmContent?.confirmLabel}
        busy={busy}
        onConfirm={answer}
        onCancel={() => setConfirm(null)}
      />
    </>
  )
}

export default PendingUnitApprovals
